<template>
  <Loading v-if="loading" />
  <Modal v-if="showModalQuickReservation" @close="closeModal">
    <div class="quickReservation">
      <div class="quickReservation__header">
        <span class="title">{{ $t('quickReservation') }}</span>
        <img
          src="@/assets/icons/CloseModal.svg"
          alt="CloseModal"
          @click="closeModal"
        />
      </div>
      <div class="quickReservation__body">
        <Dropdown
          class="quickReservation__body-dropdown"
          :selectedOption="selectedPickupBox"
          :options="pickupBoxOptions"
          @selectOption="changePickupBox"
        />
        <FormKit
          v-if="activePickupBox?.info.active && !disableContent"
          type="form"
          id="quickReservation-form"
          v-model="formData"
          :actions="false"
          @submit="submitHandler"
        >
          <div
            class="quickReservation__body-lockers"
            v-for="(locker, index) in lockers"
            :key="index"
            :data-testid="'quickReservation' + index"
          >
            <div class="lockers-info">
              <div class="size-locker">
                <p>{{ $t(locker.label) }}</p>
              </div>
              <div class="quantity-lockers">
                <p>{{ quantityLockers[locker.code] }}</p>
              </div>
            </div>

            <Input
              type="number"
              :name="locker.label"
              :max="defaultQuantityLockers[locker.code]"
              :min="0"
              :onkeydown="'return false'"
              @click="handleChange"
            />
          </div>
        </FormKit>
        <div
          v-if="!activePickupBox?.info.active"
          class="quickReservation__body-inactiveBox"
        >
          <p>{{ $t('pickupboxIsInactive') }}</p>
        </div>
        <div v-if="disableContent" class="quickReservation__body-inactiveBox">
          <p>{{ $t('noSpaceInPickupBoxes') }}</p>
        </div>
      </div>
      <div class="quickReservation__buttons">
        <Button
          class="quickReservation__buttons-button"
          :variant="
            this.defaultQuantityLockers !== this.quantityLockers
              ? 'filled'
              : 'disabled'
          "
          :title="$t('reserve')"
          :disabled="
            this.defaultQuantityLockers !== this.quantityLockers ? false : true
          "
          @click="openAddPhoneNumber"
        />
        <Button
          variant="redBordered"
          :title="$t('cancel')"
          @click="closeModal"
        />
      </div>
    </div>
  </Modal>
  <ReservedLockers
    v-if="showModalReservedLockers"
    :showModalReservedLockers="showModalReservedLockers"
    :choosedLockers="choosedLockers"
    @closeModal="closeModalReservedLockers"
    @closeAllModal="closeModal"
  />
  <AddPhoneNumber
    :showModalAddPhoneNumber="showModalAddPhoneNumber"
    @assignReservation="assignReservation"
    @closeModal="showModalAddPhoneNumber = false"
  />
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { mapActions, mapState } from 'vuex';

import Loading from '@/views/Loading.vue';
import ReservedLockers from './ReservedLockers.vue';
import AddPhoneNumber from '@/duplicatePages/AddPhoneNumber/AddPhoneNumber.vue';

import {
  DataSortLocker,
  Locker,
  LockersType,
  Module,
  PickupBox
} from '@/store/modules/pharmacy/types';
import { InputFileEvent, Option } from '@/types';

import { lockers, kolliID } from './configs';

import router from '@/router';

@Options({
  name: 'QuickReservation',
  props: ['showModalQuickReservation', 'activeReservations'],
  emits: ['closeModal'],
  components: {
    Loading,
    ReservedLockers,
    AddPhoneNumber
  },
  data() {
    return {
      formData: {
        lockerId: [],
        deliveryId: ''
      },
      loading: false,
      showModalAddPhoneNumber: false,
      selectedPickupBox: {
        value: '',
        label: ''
      },
      activePickupBox: null,
      payload: null,
      choosedLockers: [],
      validationRules: {},
      pickupBoxOptions: [],
      lockers,
      selectedLockers: 0,
      quantityLockers: { 0: 0, 1: 0, 2: 0, fridge: 0 },
      defaultQuantityLockers: { 0: 0, 1: 0, 2: 0, fridge: 0 },
      numberSelectedLockers: {},
      lockersId: [],
      kolliID,
      disableContent: false,
      showModalReservedLockers: false
    };
  },
  computed: {
    ...mapState('pharmacy', ['pickupBoxes'])
  },
  created() {
    this.loadPickupBoxes();
    this.initPickupBoxOptions();
  },
  methods: {
    ...mapActions('pharmacy', [
      'assignDelivery',
      'getPharmacy',
      'getPickupBoxesByPharmacy'
    ]),
    async loadPickupBoxes() {
      const { id } = this.$route.params;

      await this.getPickupBoxesByPharmacy(id);
    },
    initPickupBoxOptions() {
      this.loading = true;

      this.pickupBoxOptions = this.pickupBoxes.map((pickupBox: PickupBox) => ({
        value: pickupBox.info.id,
        label: pickupBox.info.name,
        img: require('@/assets/icons/PickupBox.svg')
      }));

      if (this.pickupBoxOptions[0]) {
        this.selectedPickupBox = this.pickupBoxOptions[0];
        this.activePickupBox = this.pickupBoxes[0];
        this.modules = this.activePickupBox?.info.modules;

        this.getQuantityLockers(this.modules);
      }

      this.loading = false;
    },
    changePickupBox(option: Option) {
      this.selectedPickupBox = option;
      this.activePickupBox = this.pickupBoxes.find(
        (pickupBox: PickupBox) => pickupBox.info.id === option.value
      );
      this.modules = this.activePickupBox?.info.modules;

      this.getQuantityLockers(this.modules);
    },
    getVariantButton() {
      if (this.defaultQuantityLockers !== this.quantityLockers) {
        return 'filled';
      } else {
        return 'disabled';
      }
    },
    getQuantityLockers(modules: Module[]) {
      const allModulesLockers = modules
        ?.map((module: Module) => module.lockers)
        .flat();

      const lockersActiveBox = allModulesLockers?.reduce(
        (lockers: LockersType, currentLocker: Locker) => {
          return {
            0:
              lockers[0] +
              +(
                currentLocker.type === 0 &&
                !currentLocker.order &&
                currentLocker.status !== 'inactive'
              ),
            1:
              lockers[1] +
              +(
                currentLocker.type === 1 &&
                !currentLocker.order &&
                !currentLocker.fridge &&
                currentLocker.status !== 'inactive'
              ),
            2:
              lockers[2] +
              +(
                currentLocker.type === 2 &&
                !currentLocker.order &&
                currentLocker.status !== 'inactive'
              ),
            fridge:
              lockers.fridge +
              +(
                currentLocker.fridge &&
                !currentLocker.order &&
                currentLocker.status !== 'inactive'
              )
          };
        },
        { 0: 0, 1: 0, 2: 0, fridge: 0 }
      );

      this.defaultQuantityLockers = lockersActiveBox;
      this.quantityLockers = this.defaultQuantityLockers;

      const valuesIsZero = (currentValue: number) => currentValue === 0;

      if (lockersActiveBox) {
        const valuesLockersActiveBox =
          Object.values(lockersActiveBox).every(valuesIsZero);

        if (valuesLockersActiveBox) {
          return (this.disableContent = true);
        } else {
          return (this.disableContent = false);
        }
      }
    },
    handleChange(e: InputFileEvent) {
      const { name, value } = e.target;

      this.inputValue = value;

      if (name) {
        if (+value < 0) {
          value;
        }

        if (name === 'small' && this.defaultQuantityLockers[0] !== 0) {
          this.quantityLockers = {
            ...this.quantityLockers,
            0: this.defaultQuantityLockers[0] - +value
          };
        } else if (name === 'medium' && this.defaultQuantityLockers[1] !== 0) {
          this.quantityLockers = {
            ...this.quantityLockers,
            1: this.defaultQuantityLockers[1] - +value
          };
        } else if (name === 'large' && this.defaultQuantityLockers[2] !== 0) {
          this.quantityLockers = {
            ...this.quantityLockers,
            2: this.defaultQuantityLockers[2] - +value
          };
        } else if (
          name === 'fridge' &&
          this.defaultQuantityLockers['fridge'] !== 0
        ) {
          this.quantityLockers = {
            ...this.quantityLockers,
            fridge: this.defaultQuantityLockers['fridge'] - +value
          };
        }
      }
    },
    getNumberSelectedLockers() {
      if (this.defaultQuantityLockers !== this.quantityLockers) {
        this.numberSelectedLockers = {
          0: this.defaultQuantityLockers[0] - this.quantityLockers[0],
          1: this.defaultQuantityLockers[1] - this.quantityLockers[1],
          2: this.defaultQuantityLockers[2] - this.quantityLockers[2],
          fridge:
            this.defaultQuantityLockers['fridge'] -
            this.quantityLockers['fridge']
        };
      }
    },
    getLockersId() {
      this.lockersId = [];

      const allModulesLockers = this.modules
        .map((module: Module, idx: number) =>
          module.lockers.map((locker: Locker) => ({
            ...locker,
            moduleId: module.id ?? idx
          }))
        )
        .flat();

      const selectedLockersNumber = Object.entries(
        this.numberSelectedLockers
      ).filter(
        (lockerTypeAmount: [string, unknown]) => lockerTypeAmount[1] !== 0
      );

      selectedLockersNumber.forEach((selectedLocker: [string, unknown]) => {
        const lockersOfOneType = allModulesLockers
          .filter(
            (locker: Locker) =>
              ((locker.type === +selectedLocker[0] &&
                !locker.fridge &&
                !locker.inactive) ||
                (locker.fridge && selectedLocker[0] === 'fridge')) &&
              !locker.order
          )
          .sort(
            (a: DataSortLocker, b: DataSortLocker) => a.openCount - b.openCount
          );

        if (lockersOfOneType.length) {
          const lockerData = selectedLocker[1] as number;

          for (let i = 0; i < lockerData; i++) {
            if (lockersOfOneType[i]) {
              this.lockersId.push(
                `${lockersOfOneType[i].moduleId}-${lockersOfOneType[i].position}`
              );
            }
          }
        }
      });

      return this.lockersId;
    },
    openAddPhoneNumber() {
      if (this.activeReservations.phoneNumber.length === 0) {
        this.showModalAddPhoneNumber = true;
      } else {
        this.submitHandler();
      }
    },
    assignReservation(phoneNumber: string) {
      this.showModalAddPhoneNumber = false;
      this.submitHandler(phoneNumber);
    },
    async submitHandler(phoneNumber: string) {
      this.loading = true;
      this.getNumberSelectedLockers();
      this.getLockersId();

      if (phoneNumber) {
        this.payload = {
          assignToBox: true,
          boxInfo: {
            lockerId: [...this.lockersId],
            boxId: this.activePickupBox.info.id,
            customerPhoneNumber: phoneNumber
          }
        };
      } else {
        this.payload = {
          assignToBox: true,
          boxInfo: {
            lockerId: [...this.lockersId],
            boxId: this.activePickupBox.info.id
          }
        };
      }

      const { id } = this.$route.params;

      await this.assignDelivery({
        data: this.payload,
        pharmacyId: id,
        deliveryId: this.activeReservations.deliveryId
      });

      this.choosedLockers = this.payload.boxInfo.lockerId;
      this.showModalReservedLockers = true;

      await this.getPharmacy(id);

      this.$formkit.reset('quickReservation-form');
      this.loading = false;
    },
    submitForm() {
      this.$formkit.submit('quickReservation-form');
    },
    closeModal() {
      this.$emit('closeModal');
      this.$formkit.reset('quickReservation-form');
    },
    closeModalReservedLockers() {
      this.showModalReservedLockers = false;
      router.push({ name: 'APIReservations' });
    }
  },
  watch: {
    pickupBoxes() {
      this.initPickupBoxOptions();
    }
  }
})
export default class QuickReservation extends Vue {}
</script>

<style lang="scss">
.quickReservation {
  width: 410px;
  height: 100%;
  .quickReservation__header {
    display: flex;
    justify-content: space-between;
    padding: 24px 24px 0 24px;
    .title {
      font-family: 'Ubuntu-Medium';
      font-size: $eighteenSize;
      line-height: 14px;
    }
    img {
      cursor: pointer;
    }
  }
  .quickReservation__body {
    margin: 24px;
    .quickReservation__body-dropdown {
      height: 40px;
      width: 360px;
    }
    .formkit-form {
      margin-top: 24px;
      .quickReservation__body-lockers {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 11px;
        &:last-child {
          margin-bottom: 0;
        }
        .lockers-info {
          display: flex;
          justify-content: space-between;
          align-items: center;
          width: 140px;
          .quantity-lockers {
            width: 40px;
            height: 20px;
            text-align: center;
            border: 1px solid $main-green;
            border-radius: 8px;
            p {
              border-radius: 8px;
              margin: 2px 0;
            }
          }
        }
        .input {
          width: 70px;
          height: 40px;
          margin-bottom: 0;
          .formkit-outer {
            height: 40px;
            .formkit-wrapper {
              height: 40px;
              .formkit-inner {
                .formkit-input {
                  height: 40px;
                }
              }
            }
          }
        }
      }
    }
    .quickReservation__body-inactiveBox {
      margin: 30px 0;
      p {
        color: $label;
        font-size: $fourteenSize;
        line-height: $twentyTwoSize;
        font-weight: 500;
      }
    }
  }
  .quickReservation__buttons {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 362px;
    padding: 24px;
    padding-top: 0;
    .button {
      width: 200px;
    }
    .quickReservation__buttons-button {
      margin-right: 12px;
    }
  }
}
</style>
